import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild, OnDestroy, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { ISvcChipGroupedOption, ISvcChipOption } from '@solvace/component/svc-controls';
import { ISvcUserAvatar, UserAvatarSize } from '@solvace/component/svc-user-avatar';
import { SvcMediaCarouselAddEvent, SvcMediaCarouselItem, SvcMediaCarouselRemoveEvent } from '@solvace/component/svc-media-carousel';
import { SvcToastService } from '@solvace/component/svc-toast';
import { StaticApplicationId, SvcViewsService, User, UserEnvironment, UserService, SvcGlobalDataPrefetching } from 'projects/lib-shared-core/src/public-api';
import { catchError, finalize, forkJoin, Subject, Subscription, takeUntil, tap, interval, of, take } from 'rxjs';
import { DialogAlertTabs, DialogAlertType } from './shared/enums/dialog-alert-type.enum';
import { UserRoleALert } from './shared/enums/user-role-alert.enum';
import { AlertAttachmentMedias } from './shared/interfaces/alert-attachment.interface';
import { AlertCombos } from './shared/interfaces/alert-combos-data.interface';
import { AlertBodyCreate, AlertData, AlertResponseCreate } from './shared/interfaces/alert.interface';
import { DialogAlert } from './shared/interfaces/dialog-alert.interface';
import { AlertApplicationFeedService } from './shared/services/alert-application-feed.service';
import { AlertApplicationFeed } from './shared/interfaces/alert-application-feed.interface';
import { AlertAttachmentService } from './shared/services/alert-attachment.service';
import { AlertCombosService } from './shared/services/alert-combos.service';
import { AlertErrorService } from './shared/services/alert-error.service';
import { AlertService } from './shared/services/alert.service';
import { SvcDialogPrimaryComponent, SvcDialogService } from '@solvace/component/svc-dialogs';
import { SvcPrinterService } from '../../general/svc-printer/svc-printer.service';
import { UserRoleItem } from '../../admin/svc-access-permission/models/user-role.model';
import { SvcAccessPermissionService } from '../../admin/svc-access-permission/svc-access-permission.service';
import { SvcPrinterElement } from '../../general/svc-printer/svc-printer-classes';
import { environment } from 'projects/environments/environment';
import { SvcReactionChangeEvent } from '@solvace/feature/general/svc-reaction';
import { SvcTotalsStatusComponent } from '@solvace/feature/general/svc-totals-status';
import { SvcLocationFieldComponent } from '@solvace/feature/general/svc-location-field';
import { SvcCustomFieldsRegistrationFeature } from '@solvace/feature/admin/svc-custom-fields-registration/enums/svc-custom-fields-registration.enum';
import { SvcFormCustomFieldComponent } from '@solvace/feature/general/svc-form-custom-field';

@Component({
  selector: 'svc-modal-dialog-alert',
  templateUrl: './svc-modal-alert.component.html',
  styleUrls: ['./svc-modal-alert.component.scss'],
})
export class SvcModalAlertComponent implements OnInit, OnDestroy {
  @ViewChild('inputFile', { static: false }) fileInput: ElementRef;
  @ViewChild(SvcDialogPrimaryComponent) dialog: SvcDialogPrimaryComponent;
  @ViewChild('header') header: ElementRef<HTMLElement>;
  @ViewChild('topInfo') topInfo: ElementRef<HTMLElement>;
  @ViewChild('alertTab') alertTab: ElementRef<HTMLElement>;
  @ViewChild('actionPlanTab') actionPlanTab: ElementRef<HTMLElement>;
  @ViewChild('feedTab') feedTab: ElementRef<HTMLElement>;
  @ViewChild('totalsStatus') public totalsStatus: SvcTotalsStatusComponent;
  @ViewChild('locationField') locationField: SvcLocationFieldComponent;
  @ViewChild('customField') public customField: SvcFormCustomFieldComponent;

  private readonly limitSizeFile: number = 10485760;
  private readonly limitSizeTotal: number = 104857600;
  private readonly limitQuantityFiles: number = 10;

  public env = environment;
  public configType: string;
  public environment: UserEnvironment = this._userService.environment;
  public isLoading: boolean = true;
  public isSaveLoading: boolean = false;
  public isSave: boolean = false;
  public formAlert: FormGroup;
  public tabIndex: number = 0;
  public alert: AlertData;
  public midiasCarousel: SvcMediaCarouselItem[] = [];
  public featureName = 'view-alert';
  public acceptFiles: string[] = ['image/png', 'image/jpg', 'image/jpeg', 'video/mp4', 'video/mov', 'video/3pg', 'video/webm'];
  public userAvatar: ISvcUserAvatar;
  public filesToUpload: File[] = [];
  public filesToDelete: number[] = [];
  public alertCombos: AlertCombos = {};
  public userHasPermissionToEdit: boolean;
  public userHasPermissionToDelete: boolean;
  public userLoggedId: string = this._userService.userId$;
  public openPlans: number;
  public completePlans: number;
  public dialogAlertTabs = DialogAlertTabs;
  public applicationId = StaticApplicationId.alert;
  public legacyApplicationId = StaticApplicationId.actionPlan;
  public showTabActionPlan: boolean;
  public isReactionLoading = false;
  public fromAddMode = false;
  public featureIdAlert = SvcCustomFieldsRegistrationFeature.alert;

  public showVoiceToTextButton: boolean;
  public isFailuresLoading = false;
  public isShowTabActionPlanLoading = true;
  public isShowVoiceToTextButtonLoading = true;
  public isMaterialsLoading = false;
  public isAllCombosLoading = true;
  public rolesCreatePermission: string[] = [UserRoleALert.Administrator, UserRoleALert.Analyst];
  public carouselAddTrigger: SvcMediaCarouselAddEvent;
  public maxOccurrenceDate: Date = this.normalizeDate(new Date());
  public occurrenceDateFilter = (date: Date | null): boolean => !date || this.normalizeDate(date).getTime() <= this.maxOccurrenceDate.getTime();

  private destroy$: Subject<void> = new Subject<void>();
  private getFailuresSubscription: Subscription;
  private getMaterialSubscription: Subscription;
  private getRoleByUserSubscription: Subscription;
  private persistedMedia = signal<SvcMediaCarouselItem[]>([]);
  private uploadedMedia = signal<{ item: SvcMediaCarouselItem; file: File }[]>([]);

  constructor(
    public dialogRef: MatDialogRef<SvcModalAlertComponent>,
    @Inject(MAT_DIALOG_DATA) public alertMode: DialogAlert,
    private _userService: UserService,
    private _translocoService: TranslocoService,
    private _fb: FormBuilder,
    private _toastService: SvcToastService,
    private _cd: ChangeDetectorRef,
    private _alertCombosService: AlertCombosService,
    private _alertErrorService: AlertErrorService,
    private _alertService: AlertService,
    private _alertAttachmentService: AlertAttachmentService,
    private _accessPermissionService: SvcAccessPermissionService,
    private _alertApplicationFeedService: AlertApplicationFeedService,
    private _svcViewsService: SvcViewsService,
    private _svcPrinterService: SvcPrinterService,
    private _globalDataPrefetching: SvcGlobalDataPrefetching,
    private _dialogService: SvcDialogService,
  ) {
    this.dialogRef.disableClose = true;
    dialogRef.addPanelClass('md:py-5');
    this.fromAddMode = this.alertMode?.mode === DialogAlertType.add;
  }

  public ngOnInit(): void {
    this.initFormAlert();
    this.setUserAvatar();
    this.getAlertById();
    this.getPermissionsUser();
    this.getShowTabActionPlan();
    this.getShowVoiceToText();
    this.getFormAllCombos();
    this.registerView();
  }

  public get isViewMode(): boolean {
    return this.alertMode.mode === DialogAlertType.view;
  }

  public get isEditMode(): boolean {
    return this.alertMode.mode === DialogAlertType.edit;
  }

  public get isAddMode(): boolean {
    return this.alertMode.mode === DialogAlertType.add;
  }

  public get isInitialLoading(): boolean {
    return (
      this.isLoading ||
      this.isShowTabActionPlanLoading ||
      this.isShowVoiceToTextButtonLoading ||
      this.isAllCombosLoading ||
      (this.locationField?.isLoading ?? true)
    );
  }

  private getPermissionsUser(): void {
    if (this.isViewMode) this.getRoleByUserSubscription?.unsubscribe();
    this.getRoleByUserSubscription = this._accessPermissionService.getRolesByUser(StaticApplicationId.alert).subscribe({
      next: (userRoles: UserRoleItem[]) => {
        this.userHasPermissionToEdit = userRoles.some((userRole: UserRoleItem) => userRole.roleId === UserRoleALert.Administrator || userRole.roleId === UserRoleALert.Analyst);

        this.userHasPermissionToDelete = userRoles.some((userRole: UserRoleItem) => userRole.roleId === UserRoleALert.Administrator);
      },
      error: (err) => this._alertErrorService.showErrorInToast(err),
    });
  }

  private initFormAlert(): void {
    this.formAlert = this._fb.group({
      originatorId: [this._userService.userId$, Validators.required],
      alertId: [null],
      createDate: [{ value: new Date(), disabled: true }, Validators.required],
      occurrenceDate: [null, Validators.required],
      teamId: [null],
      locationId: [null, Validators.required],
      classificationId: [null, Validators.required],
      failureId: [null, Validators.required],
      materialTypeId: [null],
      productId: [null],
      supplierId: [null],
      hashtagIds: [null],
      description: [null],
    });

    if (!this.isViewMode) this.formValueChanges();
  }

  private formValueChanges(): void {
    this.formAlert
      .get('classificationId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (classificationId: number[]) => {
          if (classificationId?.length && !this.isViewMode) this.getFailures(classificationId[0]);
          else if (!this.isViewMode) {
            this.formAlert?.controls?.failureId?.reset();
            this.alertCombos.failures = [];
          }
        },
      });

    this.formAlert
      .get('materialTypeId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (materialTypeId: number[]) => {
          if (materialTypeId?.length && !this.isViewMode) this.getMaterials(materialTypeId[0]);
          else if (!this.isViewMode) {
            this.formAlert?.controls?.productId?.reset();
            this.alertCombos.materials = [];
          }
        },
      });
  }

  private getFailures(classificationId: number, options?: { checkMaterial?: boolean }): void {
    this.isFailuresLoading = true;
    this.getFailuresSubscription?.unsubscribe();
    this.getFailuresSubscription = this._alertCombosService
      .getFailures(classificationId)
      .pipe(
        tap((failures: ISvcChipOption[]) => (this.alertCombos = { ...this.alertCombos, failures })),
        catchError((err) => {
          this._alertErrorService.showErrorInToast(err);
          return err;
        }),
        finalize(() => {
          if (options?.checkMaterial && (this.alert?.materialTypeId || this.alert?.productId)) {
            this.getMaterials(this.alert?.materialTypeId, this.alert?.productId);
          }
          this.isFailuresLoading = false;
        })
      )
      .subscribe();
  }

  private getMaterials(materialTypeId: number, productId?: number): void {
    this.isMaterialsLoading = true;
    this.getMaterialSubscription?.unsubscribe();
    this.getMaterialSubscription = this._alertCombosService
      .getMaterial(materialTypeId, productId)
      .pipe(
        tap((materials: ISvcChipOption[]) => {
          this.alertCombos = { ...this.alertCombos, materials };
        }),
        catchError((err) => {
          this._alertErrorService.showErrorInToast(err);
          return err;
        }),
        finalize(() => {
          this.isMaterialsLoading = false;
        })
      )
      .subscribe();
  }

  private setUserAvatar(): void {
    if (DialogAlertType.add === this.alertMode.mode) {
      const user: User = this._userService.user;
      this.userAvatar = {
        name: user.firstLastName,
        picture: user.pictureFile,
        initialsColor: user.initialsColor,
        size: UserAvatarSize.Table,
      };
    }
  }

  private isSameCarouselMedia(left: SvcMediaCarouselItem, right: SvcMediaCarouselItem): boolean {
    if (!left || !right) return false;

    if (left.id != null && right.id != null) return String(left.id) === String(right.id);
    if (left.url && right.url) return left.url === right.url;

    return left.name === right.name;
  }

  private refreshMediaState(): void {
    const persisted = this.persistedMedia();
    const uploaded = this.uploadedMedia();
    this.midiasCarousel = [...persisted, ...uploaded.map((media) => media.item)];
    this.filesToUpload = uploaded.map((media) => media.file);
  }

  public removeSlide(media: SvcMediaCarouselRemoveEvent): void {
    const itemToRemove = media?.item;
    if (!itemToRemove) return;

    const indexToRemove: number = this.midiasCarousel.findIndex((midia: SvcMediaCarouselItem) => this.isSameCarouselMedia(midia, itemToRemove));
    if (indexToRemove < 0) return;

    const removedMedia = this.midiasCarousel[indexToRemove];
    const isPersistedMedia = typeof removedMedia?.id === 'number';

    if (this.isEditMode && isPersistedMedia) {
      this.filesToDelete = Array.from(new Set([...(this.filesToDelete ?? []), removedMedia.id as number]));
      this.persistedMedia.update((items) => items.filter((item) => !this.isSameCarouselMedia(item, removedMedia)));
    } else {
      this.uploadedMedia.update((items) => items.filter((item) => !this.isSameCarouselMedia(item.item, removedMedia)));
    }

    this.refreshMediaState();

    if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = null;
    if (typeof media.onDeleted === 'function') {
      media.onDeleted();
    }
  }

  private fileTypeIsInvalid(files: FileList): boolean {
    const isAccept: boolean = Array.from(files).every((file: File) => this.acceptFiles.includes(file.type));

    if (!isAccept) {
      this._toastService.error(this._translocoService.translate('Tipo de arquivo não permitido'));
    }

    return !isAccept;
  }

  private getTotalFilesSize(): number {
    return this.filesToUpload.reduce((totalSize: number, file: File) => totalSize + file.size, 0);
  }

  private filesExceededLimit(files: FileList): boolean {
    let totalSize: number = this.getTotalFilesSize();
    let exceedingLimit = false;

    Array.from(files).forEach((file: File) => {
      totalSize += file.size;

      const messageFile: string =
        totalSize >= this.limitSizeTotal ? 'O tamanho total dos arquivos excede o limite de 100 Megabytes' : 'O tamanho do arquivo excede o limite de 10 Megabytes';

      if (totalSize >= this.limitSizeTotal || file.size >= this.limitSizeFile) {
        this._toastService.error(this._translocoService.translate(messageFile));
        exceedingLimit = true;
        return;
      }
    });

    return exceedingLimit;
  }

  public filesExceededQuantity(files: FileList): boolean {
    const exceededQuantity: boolean = files?.length >= this.limitQuantityFiles;

    if (exceededQuantity) this._toastService.error(this._translocoService.translate('Limite de arquivos atingidos'));

    return exceededQuantity;
  }
  public filesIsInvalid(files: FileList): boolean {
    return !files || files.length === 0 || this.fileTypeIsInvalid(files) || this.filesExceededLimit(files) || this.filesExceededQuantity(files);
  }

  public addMediaInputFile(event: Event): void {
    const files: FileList = (event.target as HTMLInputElement).files;

    if (this.filesIsInvalid(files)) return;

    Array.from(files).forEach((file: File, index: number) => this.appendMediaFile(file, files.length - 1 === index));
  }

  public print() {
    const imgs = this.midiasCarousel.filter((m) => !m.type || m.type === 'image');
    this._svcPrinterService.open({
      options: [
        { name: 'Header', elements: [this.dialog.dialogTitleContainer?.nativeElement], show: false },
        { name: 'TopInfo', elements: [this.topInfo.nativeElement], show: false },
        {
          name: this._translocoService.translate('Alerta'),
          elements: [this.alertTab.nativeElement],
          selected: true,
        },
        ...(this.actionPlanTab?.nativeElement
          ? [
              {
                name: this._translocoService.translate('Plano de ação'),
                elements: [this.actionPlanTab.nativeElement],
                selected: true,
              },
            ]
          : []),
        {
          name: this._translocoService.translate('Feed'),
          elements: [this.feedTab.nativeElement],
        },
      ],
      additional: {
        options:
          imgs.length > 0
            ? [
                {
                  name: this._translocoService.translate('Imagens'),
                  areAllImageElements: true,
                  elements: imgs.map((m) => {
                    const img = document.createElement('img');
                    img.src = m.url;
                    return <SvcPrinterElement>{
                      element: img,
                      selected: true,
                    };
                  }),
                },
              ]
            : null,
      },
    });
  }

 private appendMediaFile(file: File, lastIndex: boolean): void {
    const reader = new FileReader();
    reader.onload = (fileReader: ProgressEvent<FileReader>) => {
      const item: SvcMediaCarouselItem = {
        name: file.name,
        url: fileReader.target.result as string,
        type: file.type?.includes('video') ? 'video' : 'image',
      };

      this.uploadedMedia.update((items) => [...items, { item, file }]);
      this.refreshMediaState();

      this.carouselAddTrigger?.onAdded({
        item,
      });

      if (lastIndex) {
        this.midiasCarousel = [...this.midiasCarousel];
      }
    };
    reader.readAsDataURL(file);
  }

  private updateMidiasCarousel(): void {
    const persisted = this.alert?.attachments?.length
      ? this.alert.attachments.map((attachment: AlertAttachmentMedias) => {
          return {
            id: attachment?.attachmentId,
            url: attachment?.signedUrl,
            title: '',
            type: ['.png', '.jpg', '.jpeg', 'png', 'jpg', 'jpeg'].includes(attachment?.extension?.toLowerCase()) ? 'image' : 'video',
          } as SvcMediaCarouselItem;
        })
      : [];

    this.persistedMedia.set(persisted);
    this.uploadedMedia.set([]);
    this.filesToDelete = [];
    this.refreshMediaState();
  }

  private patchFormAlert(): void {
    this.userAvatar = {
      name: this.alert?.userAvatar?.firstLastName,
      picture: this.alert?.userAvatar?.pictureUrl,
      initialsColor: this.alert?.userAvatar?.preferenceColor,
      size: UserAvatarSize.Table,
    };

    if (this.isAllCombosLoading) {
      this.alertCombos = {
        ...this.alertCombos,
        teams: this.alert.team ? [{ text: this.alert.team, value: this.alert.teamId }] : [],
        classification: this.alert.classification ? [{ label: this.alert.classification, value: this.alert.classificationId }] : [],
        materials: this.alert.product ? [{ label: this.alert.product, value: this.alert.productId }] : [],
        suppliers: this.alert.supplier ? [{ label: this.alert.supplier, value: this.alert.supplierId }] : [],
        failures: this.alert.failure ? [{ label: this.alert.failure, value: this.alert.failureId }] : [],
        materialType: this.alert.materialType ? [{ label: this.alert.materialType, value: this.alert.materialTypeId }] : [],
        hashtags: this.alert.hashtags?.length ? <ISvcChipGroupedOption[]>[{ group: '', options: this.alert.hashtags.map((h) => ({ label: h.hashtag, value: h.hashtagId })) }] : [],
      };
    }

    this.updateMidiasCarousel();

    this.formAlert?.patchValue({
      ...this.alert,
      locationId: this.alert?.locationId ? [this.alert?.locationId] : [],
      classificationId: this.alert?.classificationId ? [this.alert?.classificationId] : [],
      teamId: this.alert?.teamId,
      failureId: this.alert?.failureId ? [this.alert?.failureId] : [],
      productId: this.alert?.productId ? [this.alert?.productId] : [],
      supplierId: this.alert?.supplierId ? [this.alert?.supplierId] : [],
      materialTypeId: this.alert.materialTypeId ? [this.alert.materialTypeId] : [],
      createDate: this.alert?.createDate,
      occurrenceDate: this.alert?.occurrenceDate,
      hashtagIds: this.alert?.hashtags?.map((h) => h.hashtagId) ?? [],
    });

    if (this.isViewMode) {
      this.formAlert?.disable({ emitEvent: false });
    }
  }

  private getAlertById(): void {
    if ((this.isViewMode || this.isEditMode || this.isSave) && (this.alertMode?.id || this.alertMode?.uniqueId)) {
      this.isLoading = true;
      
      const alertObservable = this.alertMode?.uniqueId
        ? this._alertService.getAlertByUniqueId(this.alertMode.uniqueId)
        : this._alertService.getAlertById(this.alertMode.id);

      alertObservable
        .pipe(
          takeUntil(this.destroy$),
          tap((alert: AlertData) => {
            this.alert = alert;
            this.patchFormAlert();
            if (this.alert?.classificationId) this.getFailures(this.alert.classificationId, { checkMaterial: true });
            else if (this.alert?.materialTypeId || this.alert?.productId) this.getMaterials(this.alert?.materialTypeId, this.alert?.productId);
            this.isLoading = false;
            setTimeout(() => {
              this.customField?.getCustomFieldByExternalReferenceId(null, this.alert.alertId);
            });
          }),
          catchError((err) => {
            this.showMessageError(err);
            return err;
          })
        )
        .subscribe();
    } else {
      this.isLoading = false;
    }
  }

  private getShowTabActionPlan(): void {
    this.isShowTabActionPlanLoading = true;
    this._globalDataPrefetching.data.alertShowTabActionPlan.value$
      .pipe(
        takeUntil(this.destroy$),
        tap((value) => {
          this.showTabActionPlan = value;
          this.isShowTabActionPlanLoading = false;
        })
      )
      .subscribe();
  }

  private getShowVoiceToText(): void {
    this.isShowVoiceToTextButtonLoading = true;
    const prefetchingItem = this._globalDataPrefetching.data.voiceToText;
    if (prefetchingItem.isLoading)
      prefetchingItem.isLoading$
        .pipe(
          tap((showVoiceToTextButton: boolean) => (this.showVoiceToTextButton = showVoiceToTextButton)),
          catchError((err) => {
            this._alertErrorService.showErrorInToast(err);
            return err;
          }),
          finalize(() => (this.isShowVoiceToTextButtonLoading = false)),
          takeUntil(this.destroy$)
        )
        .subscribe();
    else {
      this.showVoiceToTextButton = prefetchingItem.value;
      this.isShowVoiceToTextButtonLoading = false;
    }
  }

  private getFormAllCombos(): void {
    this.isAllCombosLoading = true;
    this._alertCombosService
      .getAllCombosToRegisterAlert()
      .pipe(
        takeUntil(this.destroy$),
        tap((alertCombos: AlertCombos) => {
          this.alertCombos = {
            ...this.alertCombos,
            ...alertCombos,
          };
        }),
        catchError((err) => {
          this._alertErrorService.showErrorInToast(err);
          return err;
        }),
        finalize(() => (this.isAllCombosLoading = false))
      )
      .subscribe();
  }

  private showSuccesMessage(): void {
    const message: string = this.isAddMode ? 'Alerta incluído com sucesso' : 'Alteração salva com sucesso';
    this._toastService.success(this._translocoService.translate(message));
    this._alertService.alertAdd$.next();

    if (this.isViewMode) {
      this.dialogRef.close();
    } else {
      this.switchToViewMode();
    }
  }

  public confirmDeletion(alertUniqueId: number): void {
    const _text = 'Tem certeza que deseja remover este alerta?';
    this._dialogService.openConfirm(_text).subscribe((confirmed: boolean) => {
      if (confirmed) this.deleteAlert(alertUniqueId);
    });
  }

  private deleteAlert(alertUniqueId): void {
    this._alertService.deleteAlert(alertUniqueId).subscribe({
      next: () => {
        this.showDeletedMessage();
        this._alertService.alertAdd$.next();

        this.dialogRef.close();
      },
      error: (err) => this.showMessageError(err),
    });
  }

  private showDeletedMessage(): void {
    this._toastService.success(this._translocoService.translate('Removido com sucesso'));
  }

  private switchToViewMode(): void {
    if (this.isAddMode && this.alertMode.id) {
      this.alertMode.mode = DialogAlertType.view;
      this.getAlertById();
      this.getPermissionsUser();
      this.setModeView();
    } else {
      this.setModeView();
    }
  }

  private setModeView() {
    this.isSaveLoading = false;
    this.alertMode.mode = DialogAlertType.view;
    this.formAlert.updateValueAndValidity();
  }

  private getChipValueByName(property: string): number {
    return this.formAlert?.value?.[property]?.[0];
  }

  private buildPaylodCreationAlert(): AlertBodyCreate {
    const formValue = { ...this.formAlert.value };
    delete formValue?.materialTypeId;
    delete formValue?._datepickerInternalControl;

    const alertBody = {
      ...formValue,
      locationId: this.getChipValueByName('locationId'),
      classificationId: this.getChipValueByName('classificationId'),
      failureId: this.getChipValueByName('failureId'),
      productId: this.getChipValueByName('productId'),
      supplierId: this.getChipValueByName('supplierId'),
    };

    if (this.isAddMode) delete alertBody?.alertId;

    return alertBody;
  }

  private showMessageError(error: HttpErrorResponse): void {
    this._alertErrorService.showErrorInToast(error);
    this.isLoading = false;
    this.isSaveLoading = false;
  }

  private removeAlertAttachments(): void {
    if (this.filesToDelete?.length) {
      forkJoin(
        this.filesToDelete.map((fileId: number) => {
          return this._alertAttachmentService.removeAlertAttachment(this.alert.alertId, fileId);
        })
      )
        .pipe(
          takeUntil(this.destroy$),
          catchError((err) => {
            this.showMessageError(err);
            return err;
          }),
          finalize(() => {
            if (!this.customField?.hasFields()) {
              this.showSuccesMessage();
              this.isSaveLoading = false;
            }
          })
        )
        .subscribe();
    } else {
      if (!this.customField?.hasFields()) {
        this.showSuccesMessage();
        this.isSaveLoading = false;
      }
    }
  }

  private updateAlertAttachments(alertId: number): void {
    if (this.filesToUpload?.length)
      this._alertAttachmentService
        .updateAlertAttachment(alertId, this.filesToUpload)
        .pipe(
          takeUntil(this.destroy$),
          catchError((err) => {
            this.showMessageError(err);
            return err;
          }),
          finalize(() => this.removeAlertAttachments())
        )
        .subscribe();
    else this.removeAlertAttachments();
  }

  private changeCustomFieldRecord(): void {
    this.customField
      ?.changeCustomFieldRecord(this.alertMode?.id)
      .pipe(
        tap(() => this.showSuccesMessage()),
        catchError((err) => {
          this.showMessageError(err);
          return err;
        }),
        finalize(() => (this.isSaveLoading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private _dispatchEventToLegacy(bodyAlert: AlertBodyCreate, alertResponse: AlertResponseCreate): void {
    const failureName: string = this.alertCombos?.failures?.find((option: ISvcChipOption) => option?.value === bodyAlert?.failureId)?.label as string;

    window.parent.dispatchEvent(
      new CustomEvent(bodyAlert?.alertId ? 'UpdateAlertCallback' : 'CreateAlertCallback', {
        detail: {
          failureName,
          alertId: alertResponse.alertId,
        },
      })
    );
  }

  public onBookmarkChanged(value: boolean): void {
    this._alertService.bookmarkStatusUpdated$.next(this.alert?.alertId);
    const messageKey = value ? 'adicionado aos' : 'removido dos';
    this._toastService.success(this._translocoService.translate(`Registro ${messageKey} favoritos com sucesso`));
  }

  public createOrUpdateAlert(): void {
    this.formAlert.markAllAsTouched();
    if (this.formAlert.valid && this.customField?.formValid()) {
      this.isSaveLoading = true;
      const _bodyAlert: AlertBodyCreate = this.buildPaylodCreationAlert();
      this._alertService
        .createOrUpateAlert(_bodyAlert)
        .pipe(
          takeUntil(this.destroy$),
          tap((alertResponse: AlertResponseCreate) => {
            this.isSave = true;
            this._dispatchEventToLegacy(_bodyAlert, alertResponse);
            this.alertMode.id = alertResponse.alertId;
            this.formAlert.get('alertId').setValue(alertResponse.alertId);
            this.updateAlertAttachments(alertResponse.alertId);
            if (this.customField?.hasFields()) {
              this.changeCustomFieldRecord();
            }
          }),
          catchError((err) => {
            this.showMessageError(err);
            return err;
          })
        )
        .subscribe();
    }
  }

  public setEditMode(): void {
    if (this.isViewMode) {
      this.isSave = false;
      this.alertMode.mode = DialogAlertType.edit;
      this.formAlert.enable();
      this.formAlert.get('createDate').disable();
      this.formAlert.get('failureId').setValue([this.alert?.failureId]);
      this.formAlert.get('productId').setValue([this.alert?.productId]);
      this.customField?.formCustomFields?.enable();

      setTimeout(() => {
        this.formValueChanges();
      }, 1000);
    } else this.createOrUpdateAlert();
  }

  public goToFeed(): void {
    if (this.tabIndex !== DialogAlertTabs.Feed) this.tabIndex = DialogAlertTabs.Feed;
  }

  private registerView(): void {
    if (this.isAddMode) return;
    this._svcViewsService
      .setViews({
        registryId: this.alertMode?.id.toString(),
        applicationId: StaticApplicationId.alert,
        featureName: 'view-alert',
      })
      .subscribe({
        next: () => {
          this._alertService.alertUpdated$.next(this.alertMode?.id);
        },
      });
  }

  public ngOnDestroy(): void {
    this.getFailuresSubscription?.unsubscribe();
    this.getMaterialSubscription?.unsubscribe();
    this.getRoleByUserSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onReactionChange(event: SvcReactionChangeEvent): void {
    if (event && !this.isReactionLoading) {
      (event.anticipated?.onReactionDone$ ?? of({ success: true }))
        .pipe(
          take(1),
          tap((res: any) => {
            if (res?.success) {
              this.totalsStatus?.refresh({ reactions: true, views: true });
              this._alertService.alertUpdated$.next(this.alertMode?.id);
            }
          })
        )
        .subscribe();
    }
  }

  private normalizeDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
}
